/**
 * Denetim kaydı, KVKK başvuruları, şube ve oda.
 *
 * Üçü tek ekranda: ikisi merkezin yasal yükümlülüğü, üçüncüsü de
 * yılda birkaç kez açılan bir kurulum ekranı. Ayrı sekmeler açmak
 * üst menüyü şişirirdi.
 *
 * DENETİM YALNIZCA MERKEZ SAHİBİNE AÇIK. "Kim neye baktı" bilgisini
 * bütün uygulayıcılara açmak, meslektaşların birbirini izlemesi
 * demek; KVKK'da hesap verme sorumluluğu merkezde ve soruyu
 * cevaplayacak olan da o. Kural sunucuda, burası sadece sekmeyi
 * gizliyor.
 */

import { useCallback, useEffect, useState } from 'react'
import { Hatirlatmalar } from './Hatirlatmalar'
import {
  ApiHatasi,
  EYLEM_ADI,
  TABLO_ADI,
  denetimApi,
  yerApi,
  api,
  type DenetimSatiri,
  type KvkkBasvurusu,
  type Yerler as YerListesi,
} from '../api'

const anZaman = (s: string) =>
  new Date(s).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })
const gun = (s: string) => new Date(s).toLocaleDateString('tr-TR', { dateStyle: 'medium' })

type Sekme = 'denetim' | 'kvkk' | 'hatirlatma' | 'yerler'

export function Kayitlar({ rol }: { rol: string }) {
  const sahip = rol === 'owner'
  /**
   * Sekreter KVKK başvurusu kaydedemiyor ve şube kuramıyor; ona
   * yalnızca hatırlatmalar açık. Asıl kural sunucuda, burası sadece
   * boş sekme göstermiyor.
   */
  const sekreter = rol === 'assistant'
  const [sekme, setSekme] = useState<Sekme>(
    sahip ? 'denetim' : sekreter ? 'hatirlatma' : 'kvkk',
  )

  return (
    <>
      <h1>Kayıtlar ve kurulum</h1>
      <div className="dugmeler" style={{ marginBottom: 12 }}>
        {sahip && (
          <button className={sekme === 'denetim' ? 'aktif' : ''} onClick={() => setSekme('denetim')}>
            Denetim kaydı
          </button>
        )}
        {!sekreter && (
          <button className={sekme === 'kvkk' ? 'aktif' : ''} onClick={() => setSekme('kvkk')}>
            KVKK başvuruları
          </button>
        )}
        <button
          className={sekme === 'hatirlatma' ? 'aktif' : ''}
          onClick={() => setSekme('hatirlatma')}
        >
          Hatırlatmalar
        </button>
        {!sekreter && (
          <button className={sekme === 'yerler' ? 'aktif' : ''} onClick={() => setSekme('yerler')}>
            Şube ve oda
          </button>
        )}
      </div>

      {sekme === 'denetim' && sahip && <Denetim />}
      {sekme === 'kvkk' && !sekreter && <Kvkk />}
      {sekme === 'hatirlatma' && <Hatirlatmalar />}
      {sekme === 'yerler' && !sekreter && <YerYonetimi sahip={sahip} />}
    </>
  )
}

/* ------------------------------------------------------------------ */

function Denetim() {
  const [kayitlar, setKayitlar] = useState<DenetimSatiri[] | null>(null)
  const [eylem, setEylem] = useState('')
  const [bas, setBas] = useState('')
  const [bit, setBit] = useState('')
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const r = await denetimApi.liste({ eylem, bas, bit, limit: 200 })
      setKayitlar(r.kayitlar)
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.durum === 403
          ? 'Denetim kaydını yalnızca merkez sahibi görebiliyor.'
          : 'Denetim kaydı alınamadı.',
      )
      setKayitlar([])
    }
  }, [eylem, bas, bit])

  useEffect(() => {
    void yukle()
  }, [yukle])

  return (
    <>
      <p className="alt">
        Kim, ne zaman, neye dokundu. Bu ekranı açmak da kayda geçiyor — her şeyi görebilen
        kişinin arkasında iz bırakmaması, denetimin anlamını bitirirdi.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      <div className="dugmeler" style={{ marginBottom: 10 }}>
        <select value={eylem} onChange={(e) => setEylem(e.target.value)}>
          <option value="">Bütün eylemler</option>
          {Object.entries(EYLEM_ADI).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <input type="date" value={bas} onChange={(e) => setBas(e.target.value)} title="Başlangıç" />
        <input type="date" value={bit} onChange={(e) => setBit(e.target.value)} title="Bitiş" />
        {(eylem || bas || bit) && (
          <button
            onClick={() => {
              setEylem('')
              setBas('')
              setBit('')
            }}
          >
            Süzgeci temizle
          </button>
        )}
      </div>

      {kayitlar === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : kayitlar.length === 0 ? (
        <div className="bilgi">Bu aralıkta kayıt yok.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Zaman</th>
              <th>Kim</th>
              <th>Ne yaptı</th>
              <th>Neye</th>
            </tr>
          </thead>
          <tbody>
            {kayitlar.map((k) => (
              <tr key={k.id}>
                <td style={{ whiteSpace: 'nowrap', color: 'var(--muted)' }}>{anZaman(k.zaman)}</td>
                <td>{kimYazi(k)}</td>
                <td>{EYLEM_ADI[k.eylem] ?? k.eylem}</td>
                <td className="alt" style={{ margin: 0 }}>
                  {k.tablo ? (TABLO_ADI[k.tablo] ?? k.tablo) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/**
 * Satırdaki aktörü yazar.
 *
 * Dört ayrı aktör alanı var (kullanıcı, veli, öğretmen, platform
 * yöneticisi) ve hangisinin dolu olduğu kimin yaptığını belirliyor.
 * Hiçbiri dolu değilse sistem yapmış demektir — hatırlatma işçisi
 * gibi.
 */
function kimYazi(k: DenetimSatiri): string {
  if (k.kullanici) return k.kullanici
  if (k.kullaniciId) return 'Silinmiş kullanıcı'
  if (k.kisiId) return 'Veli (portal)'
  if (k.ogretmenId) return 'Öğretmen (okul)'
  if (k.yoneticiId) return 'Platform yöneticisi'
  return 'Sistem'
}

/* ------------------------------------------------------------------ */

function Kvkk() {
  const [basvurular, setBasvurular] = useState<KvkkBasvurusu[] | null>(null)
  const [turAdi, setTurAdi] = useState<Record<string, string>>({})
  const [sureGun, setSureGun] = useState(30)
  const [acikGoster, setAcikGoster] = useState(true)
  const [ekleAcik, setEkleAcik] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const r = await denetimApi.basvurular(acikGoster)
      setBasvurular(r.basvurular)
      setTurAdi(r.turAdi)
      setSureGun(r.sureGun)
    } catch {
      setHata('Başvurular alınamadı.')
      setBasvurular([])
    }
  }, [acikGoster])

  useEffect(() => {
    void yukle()
  }, [yukle])

  return (
    <>
      <p className="alt">
        Veri sahibi başvuruları ve yasal süre takibi. KVKK 13. madde: başvuru en geç{' '}
        {sureGun} gün içinde sonuçlandırılır. Süreyi sistem hesaplıyor; {sureGun} günü aşan bir
        vade yazılamıyor.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      <div className="dugmeler" style={{ marginBottom: 10 }}>
        <button onClick={() => setEkleAcik((x) => !x)}>
          {ekleAcik ? 'Vazgeç' : 'Başvuru kaydet'}
        </button>
        <button onClick={() => setAcikGoster((x) => !x)}>
          {acikGoster ? 'Sonuçlananları da göster' : 'Yalnızca açık olanlar'}
        </button>
      </div>

      {ekleAcik && (
        <BasvuruEkle
          turAdi={turAdi}
          onEklendi={() => {
            setEkleAcik(false)
            void yukle()
          }}
        />
      )}

      {basvurular === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : basvurular.length === 0 ? (
        <div className="bilgi">
          {acikGoster ? 'Açık başvuru yok.' : 'Kayıtlı başvuru yok.'}
        </div>
      ) : (
        basvurular.map((b) => <Basvuru key={b.id} b={b} turAdi={turAdi} onDegisti={yukle} />)
      )}
    </>
  )
}

function Basvuru({
  b,
  turAdi,
  onDegisti,
}: {
  b: KvkkBasvurusu
  turAdi: Record<string, string>
  onDegisti: () => void
}) {
  const [acik, setAcik] = useState(false)
  const [sonuc, setSonuc] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  /** Süresi geçmiş mi — gecikmiş başvuru listede öne çıkmalı */
  const gecti = !b.sonuclandi && new Date(b.sonTarih) < new Date()
  const kalanGun = Math.ceil(
    (new Date(b.sonTarih).getTime() - Date.now()) / (24 * 3600 * 1000),
  )

  return (
    <div className="kart">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong>{turAdi[b.tur] ?? b.tur}</strong>
        <span className="alt" style={{ margin: 0 }}>
          {b.basvuran}
          {b.ad ? ` · ${b.ad} ${b.soyad} dosyası` : ' · dosyaya bağlı değil'}
        </span>
        <span style={{ marginLeft: 'auto' }}>
          {b.sonuclandi ? (
            <span className="rozet ok">Sonuçlandı</span>
          ) : gecti ? (
            <span className="rozet" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}>
              Süresi geçti
            </span>
          ) : (
            <span className="rozet">{kalanGun} gün kaldı</span>
          )}
        </span>
      </div>

      <p className="alt" style={{ margin: '6px 0 0' }}>
        Geldi: {gun(b.geldi)} · Son tarih: {gun(b.sonTarih)}
        {b.sonuclandi ? ` · Sonuçlandı: ${gun(b.sonuclandi)}` : ''}
        {b.ilgilenen ? ` · ${b.ilgilenen}` : ''}
      </p>

      {b.sonuc && (
        <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>{b.sonuc}</p>
      )}

      {!b.sonuclandi && (
        <>
          {hata && <div className="uyari">{hata}</div>}
          {acik ? (
            <div style={{ marginTop: 8 }}>
              <label>
                Ne yapıldı
                <textarea
                  rows={3}
                  value={sonuc}
                  onChange={(e) => setSonuc(e.target.value)}
                  maxLength={4000}
                  placeholder="Dosya dökümü hazırlanıp başvurana elden teslim edildi."
                />
              </label>
              <p className="alt" style={{ marginTop: -6 }}>
                Açıklama zorunlu: “çözüldü” işareti tek başına bir şey anlatmıyor, denetimde
                sorulan şey ne yapıldığı.
              </p>
              <div className="dugmeler">
                <button
                  className="birincil"
                  disabled={bekliyor || sonuc.trim().length < 3}
                  onClick={async () => {
                    setHata(null)
                    setBekliyor(true)
                    try {
                      await denetimApi.sonuclandir(b.id, sonuc.trim())
                      onDegisti()
                    } catch {
                      setHata('Kaydedilemedi.')
                    } finally {
                      setBekliyor(false)
                    }
                  }}
                >
                  {bekliyor ? 'Kaydediliyor…' : 'Sonuçlandır'}
                </button>
                <button onClick={() => setAcik(false)} disabled={bekliyor}>
                  Vazgeç
                </button>
              </div>
            </div>
          ) : (
            <div className="dugmeler">
              <button onClick={() => setAcik(true)}>Sonuçlandır</button>
              {b.danisanId && (
                <a href={denetimApi.dokumYolu(b.danisanId)} target="_blank" rel="noreferrer">
                  <button type="button">Dosya dökümünü al</button>
                </a>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function BasvuruEkle({
  turAdi,
  onEklendi,
}: {
  turAdi: Record<string, string>
  onEklendi: () => void
}) {
  const [tur, setTur] = useState('access')
  const [basvuran, setBasvuran] = useState('')
  const [danisanId, setDanisanId] = useState('')
  const [geldi, setGeldi] = useState(new Date().toISOString().slice(0, 10))
  const [danisanlar, setDanisanlar] = useState<{ id: string; ad: string; soyad: string }[]>([])
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  useEffect(() => {
    api
      .danisanlar()
      .then((r) =>
        setDanisanlar(
          r.danisanlar.map((d) => ({ id: d.id, ad: d.ad, soyad: d.soyad })),
        ),
      )
      .catch(() => undefined)
  }, [])

  return (
    <form
      className="kart"
      onSubmit={async (e) => {
        e.preventDefault()
        setHata(null)
        setBekliyor(true)
        try {
          await denetimApi.basvuruEkle({
            tur,
            basvuran: basvuran.trim(),
            danisanId: danisanId || null,
            geldi,
          })
          onEklendi()
        } catch {
          setHata('Kaydedilemedi.')
        } finally {
          setBekliyor(false)
        }
      }}
    >
      {hata && <div className="uyari">{hata}</div>}
      <div className="satir">
        <label>
          Başvuru türü
          <select value={tur} onChange={(e) => setTur(e.target.value)}>
            {Object.entries(turAdi).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label>
          Başvuran
          <input
            value={basvuran}
            onChange={(e) => setBasvuran(e.target.value)}
            required
            minLength={2}
            placeholder="Ayşe Yılmaz (anne)"
          />
        </label>
        <label>
          Hangi dosya
          <select value={danisanId} onChange={(e) => setDanisanId(e.target.value)}>
            <option value="">Dosyaya bağlı değil</option>
            {danisanlar.map((d) => (
              <option key={d.id} value={d.id}>
                {d.ad} {d.soyad}
              </option>
            ))}
          </select>
        </label>
        <label>
          Geliş tarihi
          <input
            type="date"
            value={geldi}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setGeldi(e.target.value)}
            required
          />
        </label>
      </div>
      <p className="alt" style={{ marginTop: -6 }}>
        Son tarih geliş tarihine göre kendiliğinden hesaplanıyor.
      </p>
      <button className="birincil" type="submit" disabled={bekliyor}>
        {bekliyor ? 'Kaydediliyor…' : 'Kaydet'}
      </button>
    </form>
  )
}

/* ------------------------------------------------------------------ */

/**
 * Şube ve oda.
 *
 * Uçlar baştan beri vardı ama onları çağıran bir ekran yoktu: ikinci
 * odası olan bir merkez onu tanımlayamıyordu. Oda çakışması
 * veritabanı kısıtıyla korunuyor ama odayı kuracak yer yoktu.
 */
function YerYonetimi({ sahip }: { sahip: boolean }) {
  const [t, setT] = useState<YerListesi | null>(null)
  const [subeAd, setSubeAd] = useState('')
  const [odaAd, setOdaAd] = useState<Record<string, string>>({})
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      setT(await api.yerler())
    } catch {
      setHata('Şube ve oda listesi alınamadı.')
    }
  }, [])

  useEffect(() => {
    void yukle()
  }, [yukle])

  const sar = async (is: () => Promise<unknown>, hataMetni: string) => {
    setHata(null)
    try {
      await is()
      await yukle()
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.kod === 'odanin_randevusu_var'
          ? `Bu odada ileri tarihli ${e.govde?.['adet'] ?? ''} randevu var. Önce onları başka odaya taşıyın.`
          : e instanceof ApiHatasi && e.kod === 'once_odalari_arsivle'
            ? 'Şubenin açık odaları var; önce odaları arşivleyin.'
            : hataMetni,
      )
    }
  }

  if (!t) return <p className="alt">{hata ?? 'Yükleniyor…'}</p>

  return (
    <>
      <p className="alt">
        Randevu ekranındaki oda listesi buradan geliyor. Aynı odada aynı saatte iki seans
        veritabanı düzeyinde engelleniyor — oda tanımlamak o korumayı kullanılabilir kılıyor.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      {t.subeler.map((s) => (
        <div className="kart" key={s.id}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <strong>{s.ad}</strong>
            {s.adres && (
              <span className="alt" style={{ margin: 0 }}>
                {s.adres}
              </span>
            )}
            {sahip && (
              <button
                style={{ marginLeft: 'auto' }}
                onClick={() => void sar(() => yerApi.subeArsivle(s.id), 'Arşivlenemedi.')}
              >
                Şubeyi arşivle
              </button>
            )}
          </div>

          <table>
            <tbody>
              {t.odalar
                .filter((o) => o.subeId === s.id)
                .map((o) => (
                  <tr key={o.id}>
                    <td>{o.ad}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        onClick={async () => {
                          const yeni = prompt('Odanın yeni adı', o.ad)
                          if (yeni && yeni.trim() && yeni.trim() !== o.ad) {
                            await sar(
                              () => yerApi.odaGuncelle(o.id, yeni.trim()),
                              'Değiştirilemedi.',
                            )
                          }
                        }}
                      >
                        Adını değiştir
                      </button>{' '}
                      {sahip && (
                        <button
                          onClick={() => void sar(() => yerApi.odaArsivle(o.id), 'Arşivlenemedi.')}
                        >
                          Arşivle
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          <div className="dugmeler">
            <input
              placeholder="Yeni oda adı"
              value={odaAd[s.id] ?? ''}
              onChange={(e) => setOdaAd({ ...odaAd, [s.id]: e.target.value })}
            />
            <button
              disabled={!(odaAd[s.id] ?? '').trim()}
              onClick={async () => {
                await sar(() => api.odaEkle(s.id, (odaAd[s.id] ?? '').trim()), 'Eklenemedi.')
                setOdaAd({ ...odaAd, [s.id]: '' })
              }}
            >
              Oda ekle
            </button>
          </div>
        </div>
      ))}

      <div className="kart">
        <strong style={{ fontSize: 13, color: 'var(--muted)' }}>Yeni şube</strong>
        <div className="dugmeler" style={{ marginTop: 8 }}>
          <input
            placeholder="Şube adı"
            value={subeAd}
            onChange={(e) => setSubeAd(e.target.value)}
          />
          <button
            disabled={!subeAd.trim()}
            onClick={async () => {
              await sar(() => api.subeEkle(subeAd.trim()), 'Eklenemedi.')
              setSubeAd('')
            }}
          >
            Şube ekle
          </button>
        </div>
      </div>
    </>
  )
}
